import React, { useEffect, useMemo } from "react";
import {
  useTranslate,
  useGetIdentity,
  useOne,
  useUpdate,
} from "@refinedev/core";
import { useSelect } from "@refinedev/antd";
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Space,
  Button,
  Spin,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import {
  TunerRole,
  UserRole,
  SuperadminRole,
  ServiceRole,
} from "../../models/user";

type UserEditModalProps = {
  open: boolean;
  userId: number | string | null;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const UserEditModal: React.FC<UserEditModalProps> = ({
  open,
  userId,
  onCancel,
  onSuccess,
}) => {
  const translate = useTranslate();
  const { data: currentUser } = useGetIdentity();
  const currentUserRoleId = (currentUser as any)?.role?.id;
  const isSuperAdmin =
    currentUserRoleId === SuperadminRole.id ||
    currentUserRoleId === String(SuperadminRole.id) ||
    Number(currentUserRoleId) === SuperadminRole.id;

  const [form] = Form.useForm();

  const { data, isLoading } = useOne({
    resource: "users",
    id: userId ?? "",
    queryOptions: {
      enabled: open && !!userId,
    },
  });

  const record = data?.data;

  const { mutate: updateUser, isLoading: isUpdating } = useUpdate();

  const selectedRoleId = Form.useWatch(["role", "id"], form);

  const { selectProps: roleSelectProps } = useSelect({
    resource: "roles",
    defaultValue: record?.role?.id,
    optionLabel: "name",
  });

  const filteredRoleOptions = useMemo(() => {
    if (!roleSelectProps.options) return [];
    let options = roleSelectProps.options;

    if (!isSuperAdmin) {
      const currentTargetUserRoleId = record?.role?.id;
      options = options.filter((option: any) => {
        const roleId = option.value;
        if (roleId === SuperadminRole.id || roleId === ServiceRole.id) {
          return roleId === currentTargetUserRoleId;
        }
        return true;
      });
    }

    return options.map((option: any) => {
      const roleName = option.label || option.name;
      const translationKey = `users.fields.roleValues.${roleName}`;
      return {
        ...option,
        label: translate(translationKey, roleName),
      };
    });
  }, [roleSelectProps.options, isSuperAdmin, record?.role?.id, translate]);

  const { selectProps: accountSelectProps } = useSelect({
    resource: "accounts",
    defaultValue: record?.account?.id,
    optionLabel: "name",
    queryOptions: {
      enabled: selectedRoleId === UserRole.id,
    },
  });

  const { selectProps: serviceProviderSelectProps } = useSelect({
    resource: "service-providers",
    defaultValue: record?.serviceProvider?.id,
    optionLabel: "name",
    queryOptions: {
      enabled: selectedRoleId === TunerRole.id,
    },
  });

  const { selectProps: tenantSelectProps } = useSelect({
    resource: "tenants",
    defaultValue: record?.tenant?.id,
    optionLabel: "name",
    queryOptions: {
      enabled: isSuperAdmin,
    },
  });

  useEffect(() => {
    if (record && open) {
      const initialValues: any = {
        email: record.email,
        firstName: record.firstName,
        lastName: record.lastName,
        status: { id: record.status?.id },
        role: { id: record.role?.id },
      };

      if (record.account?.id) {
        initialValues.account = { id: record.account.id };
      }
      if (record.serviceProvider?.id) {
        initialValues.serviceProviderId = record.serviceProvider.id;
      }
      if (record.tenant?.id) {
        initialValues.tenantId = record.tenant.id;
      }

      form.setFieldsValue(initialValues);
    }

    if (!open) {
      form.resetFields();
    }
  }, [record, open, form]);

  const handleFormValuesChange = (changedValues: any) => {
    if ("role" in changedValues && changedValues.role?.id !== undefined) {
      const roleId = changedValues.role.id;
      if (roleId !== TunerRole.id) {
        form.setFieldsValue({
          serviceProviderId: undefined,
        });
      }
      if (roleId !== UserRole.id) {
        form.setFieldsValue({
          account: undefined,
        });
      }
    }
  };

  const handleFinish = (values: any) => {
    if (!userId) return;

    updateUser(
      {
        resource: "users",
        id: userId,
        values,
      },
      {
        onSuccess: () => {
          form.resetFields();
          onCancel();
          onSuccess?.();
        },
        onError: (error: any) => {
          const errors = error?.response?.data?.errors;
          if (errors && "email" in errors) {
            if (errors["email"] === "emailAlreadyExists") {
              form.setFields([
                {
                  name: "email",
                  errors: [translate("users.errors.emailAlreadyExists")],
                },
              ]);
            }
          }
        },
      },
    );
  };

  return (
    <Modal
      title={translate("users.titles.edit")}
      open={open}
      onCancel={onCancel}
      width={700}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{translate("buttons.cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isUpdating}
            onClick={() => form.submit()}
          >
            {translate("buttons.save")}
          </Button>
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormValuesChange}
          onFinish={handleFinish}
          className="!mt-4"
        >
          <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={translate("users.fields.email")}
                name={["email"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={translate("users.fields.status")}
                name={["status", "id"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  options={[
                    {
                      label: translate("users.fields.statusValues.active"),
                      value: 1,
                    },
                    {
                      label: translate("users.fields.statusValues.inactive"),
                      value: 2,
                    },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={translate("users.fields.firstName")}
                name={["firstName"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={translate("users.fields.lastName")}
                name={["lastName"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={translate("users.fields.password")}
                name={["password"]}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || value.length >= 6) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(translate("fields.errors.minLength", { min: 6 })),
                      );
                    },
                  },
                ]}
              >
                <Input.Password />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={translate("users.fields.role")}
                name={["role", "id"]}
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select {...roleSelectProps} options={filteredRoleOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              {selectedRoleId === UserRole.id && (
                <Form.Item
                  label={translate("users.fields.account")}
                  name={["account", "id"]}
                  rules={[
                    {
                      required: false,
                    },
                  ]}
                >
                  <Select {...accountSelectProps} />
                </Form.Item>
              )}
              {selectedRoleId === TunerRole.id && (
                <Form.Item
                  label={translate("users.fields.serviceProvider")}
                  name="serviceProviderId"
                  rules={[
                    {
                      required: false,
                    },
                  ]}
                >
                  <Select {...serviceProviderSelectProps} />
                </Form.Item>
              )}
            </Col>
          </Row>
          {isSuperAdmin && (
            <Row gutter={{ xs: 8, sm: 16, md: 24 }}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={translate("users.fields.tenant")}
                  name="tenantId"
                  rules={[
                    {
                      required: false,
                    },
                  ]}
                >
                  <Select {...tenantSelectProps} />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

